import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import { t } from "../i18n.js";
import Model3D from "../Model3D.jsx";
import { ensureCtx, out } from "../audio.js";
import { offerAvailable, claimReward } from "../rewards.js";

/* ==================== FODBOLD – 8 skud mod målmanden =========================
   Same controls as the other booths: take a ball from the tray, place it
   between the lines, drag back to shoot. Drag in a curve for spin (skru).
   The keeper slides (limited) — corners are open — but he escalates: feints
   at 2 goals, speeds up at 3, and goes all-out on the 8th (last) shot.
   8 shots. 3-4 goals = small, 5 = medium, 6-7 = big, all 8 = GOLD TROPHY.
================================================================================ */

const W = 360, H = 540;
const BALL_R = 10;
const START_Y = H - 76;
const ZONE_TOP = H - 110;
const ZONE_BOT = START_Y + BALL_R + 4;
const PLACE_MIN_Y = ZONE_TOP + BALL_R + 2;
const PLACE_MAX_Y = ZONE_BOT - BALL_R - 2;
const TRAY = { x: W - 38, y: H - 30, r: 30 };
const GRAB_R = 26;               // near-ball drags decide by direction: down = shoot, sideways/up = move

const GOAL_L = 48, GOAL_R = 312;   // posts
const CROSS_Y = 46;                // crossbar
const GOAL_LINE = 116;             // the line to beat
const KEEP_W = 26;                 // keeper reach (half-width)
const SHOTS = 8;
const NEED = 3;                    // goals for a prize

/* 3-4 = small, 5 = medium, 6-7 = big; 8/8 is the trophy, handled separately */
const prizeForGoals = (goals) => (goals >= 6 ? "stor" : goals >= 5 ? "mellem" : "lille");

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(out()); o.start(t); o.stop(t + dur);
  };
  return {
    unlock: ensure,
    launch() { const c = ensure(); if (c) tone(180, c.currentTime, 0.12, "triangle", 0.1); },
    knock() { const c = ensure(); if (c) tone(300, c.currentTime, 0.06, "square", 0.08); },
    goal() {
      const c = ensure(); if (!c) return;
      tone(520, c.currentTime, 0.18, "triangle", 0.16);
      tone(780, c.currentTime + 0.1, 0.26, "triangle", 0.12);
    },
    save() { const c = ensure(); if (c) tone(160, c.currentTime, 0.2, "square", 0.1); },
    hatty() {
      const c = ensure(); if (!c) return;
      [659, 784, 988].forEach((f, i) => tone(f, c.currentTime + i * 0.09, 0.22, "triangle", 0.14));
    },
    buzzer() { const c = ensure(); if (c) tone(110, c.currentTime, 0.7, "sawtooth", 0.16); },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
  };
}

export default function Football({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | failed | finished
  const [hud, setHud] = useState({ shots: 0, goals: 0 });
  const [claimed, setClaimed] = useState(null);
  const [finalGoals, setFinalGoals] = useState(0);
  const [finalShots, setFinalShots] = useState(SHOTS);
  const [offer, setOffer] = useState(false);     // rewarded "extra shot" offer
  const [adErr, setAdErr] = useState(false);
  const gameRef = useRef(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setHud({ shots: 0, goals: 0 });
    setClaimed(null);
    setFinalGoals(0);
    setFinalShots(SHOTS);
    setOffer(false);
    setAdErr(false);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* "one goal from a better prize": +1 shot for a video. The trophy is
     guarded elsewhere: 8/8 must be scored on the ORDINARY shots. */
  const declineOffer = () => {
    setOffer(false);
    const g = gameRef.current;
    if (!g) return;
    setFinalGoals(g.goals);
    setFinalShots(g.shots);
    if (g.goals >= NEED) { audioRef.current.win(); setPhase("finished"); }
    else { audioRef.current.buzzer(); setPhase("failed"); }
  };
  const watchAd = () => {
    setAdErr(false);
    claimReward("ekstra_skud", {
      onReward: () => {
        const g = gameRef.current;
        if (g) { g.bonusShots += 1; g.bonusUsed = true; g.over = false; }
        setOffer(false);
      },
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      balls: [],
      carry: null,
      pending: null,    // {ball, sx, sy} touch near the ball, direction not decided yet
      aim: null,        // {ball, sx, sy, dx, dy, path}
      popups: [],
      netHits: [],      // mål-indslag: nettet blafrer omkring punktet

      shots: 0,
      goals: 0,
      keepX: W / 2,
      keepPhase: Math.random() * Math.PI * 2,
      keepFreq: 1.45 + Math.random() * 0.45,
      keepAmp: 80 + Math.random() * 14,
      keepDir: 1,
      feintAt: null,     // next random feint (armed at 2+ goals)
      dive: null,        // the vain TV-save at the final goal: {t, dir, fromX, targetX}
      banner: { txt: t("f_banner", { shots: SHOTS, need: NEED }), t: 0 },
      streak: 0,         // consecutive goals: 3 = hat-trick, 6 = double
      hattrick: null,    // {t, title, sub, conf[]} celebration overlay
      bonusShots: 0,     // ad-bought extra shots (max 1 per match)
      bonusUsed: false,  // trophy guard: 8/8 must be ordinary shots
      extraOffered: false,
      over: false,
      now: 0,
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__fb = g;

    /* ---- canvas sizing ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- curve (skru) from the drag path ---- */
    const pathCurve = (path) => {
      if (!path || path.length < 5) return 0;
      const a0 = path[0], a1 = path[path.length - 1];
      const chx = a1.x - a0.x, chy = a1.y - a0.y;
      const L = Math.hypot(chx, chy);
      if (L < 20) return 0;
      let maxDev = 0;
      for (const p of path) {
        const dev = (chx * (p.y - a0.y) - chy * (p.x - a0.x)) / L;
        if (Math.abs(dev) > Math.abs(maxDev)) maxDev = dev;
      }
      return Math.max(-1, Math.min(1, maxDev / 40));
    };

    /* ---- input (same pattern as the other booths) ---- */
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) / (r.width / W), y: (t.clientY - r.top) / (r.height / H) };
    };
    const placedCount = () => g.balls.filter((b) => b.phase === "placed").length;
    const trayCount = () => Math.max(0, SHOTS + g.bonusShots - g.shots - placedCount() - (g.carry ? 1 : 0));
    const down = (e) => {
      audioRef.current.unlock();
      if (g.over) return;
      e.preventDefault();
      const p = pos(e);
      const pb = g.balls.find((b) => b.phase === "placed" && Math.hypot(p.x - b.x, p.y - b.y) < GRAB_R);
      if (pb) {
        g.pending = { ball: pb, sx: p.x, sy: p.y };
        return;
      }
      // one ball out of the tray at a time — penalty rhythm
      if (trayCount() > 0 && g.balls.length === 0 &&
          Math.hypot(p.x - TRAY.x, p.y - TRAY.y) < TRAY.r + 8) {
        g.carry = { x: p.x, y: p.y };
        return;
      }
      const placed = g.balls.filter((b) => b.phase === "placed");
      if (placed.length) {
        g.aim = { ball: placed[0], sx: p.x, sy: p.y, dx: 0, dy: 0, path: [{ x: p.x, y: p.y }] };
      }
    };
    const clampCarry = (p) => ({
      x: Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, p.x)),
      y: Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, p.y)),   // stays in the zone: drop = what you see
    });
    const move = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      const p = pos(e);
      if (g.pending) {
        const dx = p.x - g.pending.sx, dy = p.y - g.pending.sy;
        if (Math.hypot(dx, dy) > 12) {
          const ball = g.pending.ball;
          if (dy > Math.abs(dx)) {
            g.aim = {
              ball, sx: g.pending.sx, sy: g.pending.sy, dx, dy,
              path: [{ x: g.pending.sx, y: g.pending.sy }, { x: p.x, y: p.y }],
            };
          } else {
            g.balls = g.balls.filter((b) => b !== ball);
            g.carry = clampCarry(p);
          }
          g.pending = null;
        }
        return;
      }
      if (g.aim) {
        g.aim.dx = p.x - g.aim.sx;
        g.aim.dy = p.y - g.aim.sy;
        if (g.aim.path.length < 60) g.aim.path.push({ x: p.x, y: p.y });
      } else {
        const c = clampCarry(p);
        g.carry.x = c.x; g.carry.y = c.y;
      }
    };
    const up = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      if (g.pending) { g.pending = null; return; }   // a plain tap: ball stays put
      if (g.carry) {
        let x = Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, g.carry.x));
        const y = Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, g.carry.y));
        g.carry = null;
        g.balls.push({ x, y, vx: 0, vy: 0, phase: "placed", sink: null });
        return;
      }
      const { ball, dx, dy, path } = g.aim;
      const curve = pathCurve(path);
      g.aim = null;
      const len = Math.hypot(dx, dy);
      if (len < 14 || dy < 6) return;
      const power = Math.min(len, 190) / 190;
      const ang = Math.atan2(-dy, -dx);
      const speed = 300 + power * 620;
      ball.vx = Math.cos(ang) * speed;
      ball.vy = Math.sin(ang) * speed;
      ball.spin = -curve * 2.6;            // rad/s — the ball bends to the side the drag bows
      // full power (red on the meter) risks sailing into the crossbar
      ball.overshoot = power > 0.75 && Math.random() < ((power - 0.75) / 0.25) * 0.65;
      ball.phase = "rolling";
      g.shots += 1;
      setHud({ shots: g.shots, goals: g.goals });
      audioRef.current.launch();
    };
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", up, { passive: false });

    /* ---- physics ---- */
    const stepBall = (b, dt) => {
      if (b.phase === "placed") return;
      if (b.phase === "sinking") {
        b.sink.t += dt;
        if (b.sink.t > 0.7) b.dead = true;
        return;
      }
      // spin bends the ball while it travels upfield: rotate the velocity
      // vector (Magnus-style) so it flies a true banana arc at constant speed
      if (b.spin && b.vy < 0) {
        const a = b.spin * dt;
        const c = Math.cos(a), s = Math.sin(a);
        const nvx = b.vx * c - b.vy * s;
        b.vy = b.vx * s + b.vy * c;
        b.vx = nvx;
      }
      b.vy += 90 * dt;                       // gentle slope back toward the player
      const damp = Math.exp(-0.5 * dt);
      b.vx *= damp; b.vy *= damp;
      b.x += b.vx * dt; b.y += b.vy * dt;

      // side rails
      if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
      if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }

      // the goal line: goal, save, post or boards
      if (b.vy < 0 && b.y <= GOAL_LINE + BALL_R) {
        const inMouth = b.x > GOAL_L + BALL_R && b.x < GOAL_R - BALL_R;
        const postHit = Math.abs(b.x - GOAL_L) <= BALL_R + 2 || Math.abs(b.x - GOAL_R) <= BALL_R + 2;
        if (b.overshoot && (inMouth || postHit)) {
          // sailed too high: crossbar!
          b.overshoot = false;
          b.y = GOAL_LINE + BALL_R;
          b.vy = Math.abs(b.vy) * 0.65;
          b.vx *= 0.9;
          b.spin = 0;
          g.popups.push({ x: b.x, y: GOAL_LINE - 6, txt: t("pop_over"), c: "#ffd75e", t: 0 });
          audioRef.current.knock();
        } else if (postHit) {
          b.y = GOAL_LINE + BALL_R;
          b.vy = Math.abs(b.vy) * 0.5;
          b.vx = (b.x < W / 2 ? 1 : -1) * Math.max(Math.abs(b.vx), 60);
          g.popups.push({ x: b.x, y: GOAL_LINE - 6, txt: t("pop_post"), c: "#f2f2f0", t: 0 });
          audioRef.current.knock();
        } else if (inMouth) {
          if (Math.abs(b.x - g.keepX) < KEEP_W + BALL_R * 0.7) {
            // saved!
            b.y = GOAL_LINE + BALL_R;
            b.vy = Math.abs(b.vy) * 0.55;
            b.vx += (b.x - g.keepX) * 6;
            b.spin = 0;
            g.popups.push({ x: b.x, y: GOAL_LINE - 6, txt: t("pop_saved"), c: "#cfd4d8", t: 0 });
            audioRef.current.save();
          } else {
            // GOAL!
            b.phase = "sinking";
            b.scored = true;
            b.sink = { x: b.x, y: Math.max(CROSS_Y + 18, GOAL_LINE - 26), t: 0 };
            g.netHits.push({ x: b.sink.x, y: b.sink.y, t: 0 });   // nettet blafrer her
            g.goals += 1;
            setHud({ shots: g.shots, goals: g.goals });
            g.popups.push({ x: b.x, y: GOAL_LINE - 10, txt: t("pop_goal"), c: "#3ddc73", t: 0 });
            audioRef.current.goal();
            // 3 in a row = hat-trick, 6 in a row = double hat-trick
            g.streak += 1;
            if (g.streak === 3 || g.streak === 6) {
              const dbl = g.streak === 6;
              g.hattrick = {
                t: 0,
                title: t(dbl ? "f_hattrick2" : "f_hattrick"),
                sub: t("f_hattrick_sub", { n: g.streak }),
                conf: Array.from({ length: 40 }, () => ({
                  x: W / 2 + (Math.random() * 240 - 120),
                  y: 200 + Math.random() * 30,
                  vx: Math.random() * 130 - 65,
                  vy: -70 - Math.random() * 120,
                  c: ["#E3B923", "#C8352E", "#3ddc73", "#4aa3e8", "#F6EED9"][Math.floor(Math.random() * 5)],
                  r: Math.random() * Math.PI,
                })),
              };
              audioRef.current[dbl ? "win" : "hatty"]();
            }
            // the final goal: he throws himself after it — a bad TV save
            if (g.goals >= SHOTS)
              g.dive = { t: 0, dir: b.x >= g.keepX ? 1 : -1, fromX: g.keepX, targetX: b.x };
          }
        } else {
          // boards beside the goal
          b.y = GOAL_LINE + BALL_R;
          b.vy = Math.abs(b.vy) * 0.5;
          audioRef.current.knock();
        }
      }

      // back at the bottom, or stalled: shot is spent
      if (b.y > H - 30 && b.vy > 0) b.dead = true;
      const spd = Math.hypot(b.vx, b.vy);
      if (spd < 15) { b.slowT = (b.slowT || 0) + dt; if (b.slowT > 0.8) b.dead = true; }
      else b.slowT = 0;
    };

    const step = (dt) => {
      g.now += dt;
      // the keeper slides — limited, so the corners stay open.
      // After 3 goals he wakes up and moves faster (phase-integrated: no jump).
      // After 2 goals he also FEINTS: at random moments he cuts and goes the
      // other way instead of continuing the static pattern.
      if (g.dive) {
        // hopeless dive after the 5th goal — he stretches, but falls short
        g.dive.t += dt;
        const k = Math.min(g.dive.t * 2.2, 1);
        const ease = 1 - Math.pow(1 - k, 2);
        const short = Math.max(GOAL_L + 34, Math.min(GOAL_R - 34, g.dive.targetX - g.dive.dir * 46));
        g.keepX = g.dive.fromX + (short - g.dive.fromX) * ease;
      } else {
        if (g.goals >= 2 && !g.over) {
          if (g.feintAt === null) g.feintAt = g.now + 0.7 + Math.random() * 1.8;
          if (g.now >= g.feintAt) {
            g.keepDir *= -1;
            g.feintAt = g.now + 0.7 + Math.random() * 1.8;
          }
        }
        // escalation: 1.65× after 3 goals — and on the LAST shot he gives everything
        let mult = g.goals >= 3 ? 1.65 : 1;
        if (g.shots >= SHOTS) mult *= 1.4;             // the 8th ball is in play
        g.keepPhase += g.keepFreq * mult * g.keepDir * dt;
        g.keepX = W / 2 + Math.sin(g.keepPhase) * g.keepAmp;
      }

      for (const b of g.balls) stepBall(b, dt);
      for (const b of g.balls) if (b.dead && !b.scored) g.streak = 0;   // a miss breaks the run
      g.balls = g.balls.filter((b) => !b.dead);

      // all shots resolved?
      if (!g.over && g.shots >= SHOTS + g.bonusShots && g.balls.length === 0 && !g.carry && !g.aim) {
        g.over = true;
        // one goal short of the next prize tier (3/5/6, NEVER the trophy at 8):
        // offer +1 shot for a video before calling the match
        if (!g.extraOffered && (g.goals === 2 || g.goals === 4 || g.goals === 5)
            && offerAvailable("ekstra_skud")) {
          g.extraOffered = true;
          setOffer(true);
        } else {
          setFinalGoals(g.goals);
          setFinalShots(g.shots);
          if (g.goals >= SHOTS && !g.bonusUsed) {
            // a perfect game on the ORDINARY shots: the gold trophy, no picker
            const trophy = saveTrophy({ game: "Fodbold" });
            setClaimed(trophy);
            audioRef.current.win();
            setTimeout(() => setPhase("finished"), 900);
          } else if (g.goals >= NEED) {
            audioRef.current.win();
            setTimeout(() => setPhase("finished"), 900);
          } else {
            audioRef.current.buzzer();
            setTimeout(() => setPhase("failed"), 900);
          }
        }
      }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1.1);
      g.netHits.forEach((hh) => (hh.t += dt));
      g.netHits = g.netHits.filter((hh) => hh.t < 1.2);
      if (g.banner) { g.banner.t += dt; if (g.banner.t > 1.8) g.banner = null; }
      if (g.hattrick) {
        g.hattrick.t += dt;
        for (const f of g.hattrick.conf) {
          f.vy += 300 * dt;
          f.x += f.vx * dt; f.y += f.vy * dt; f.r += 3 * dt;
        }
        if (g.hattrick.t > 2.6) g.hattrick = null;
      }
    };

    /* ---- drawing ---- */
    const drawBallShape = (x, y, r) => {
      // little football: white with dark patches
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = "#f4f4f2"; ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.25)"; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = "#2b2b2b";
      ctx.beginPath(); ctx.arc(x, y, r * 0.32, 0, Math.PI * 2); ctx.fill();
      [[0.7, -0.3], [-0.62, -0.45], [-0.1, 0.75]].forEach(([ox, oy]) => {
        ctx.beginPath(); ctx.arc(x + ox * r, y + oy * r, r * 0.2, 0, Math.PI * 2); ctx.fill();
      });
    };

    const draw = () => {
      // pitch: nattekamp-look m. perspektiv (Gemini-mockuppet, tegnet figur-stil)
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#3e7229");
      grad.addColorStop(0.45, "#4e8a33");
      grad.addColorStop(1, "#67a542");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      // klippede baner i perspektiv: striberne bliver bredere mod bunden
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      let sy = CROSS_Y, sh = 14, band = 0;
      while (sy < H) {
        if (band % 2 === 0) ctx.fillRect(0, sy, W, sh);
        sy += sh; sh *= 1.18; band++;
      }
      for (let i = 0; i < 150; i++) {          // græs-tekstur
        ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.03)" : "rgba(0,30,0,0.05)";
        ctx.fillRect((i * 89) % W, (i * 197) % H, 1.6, 1.6);
      }
      // let sidelys-fald mod kanterne
      const sideV = ctx.createLinearGradient(0, 0, W, 0);
      sideV.addColorStop(0, "rgba(0,0,0,0.16)");
      sideV.addColorStop(0.2, "rgba(0,0,0,0)");
      sideV.addColorStop(0.8, "rgba(0,0,0,0)");
      sideV.addColorStop(1, "rgba(0,0,0,0.16)");
      ctx.fillStyle = sideV;
      ctx.fillRect(0, 0, W, H);

      // pitch markings in real proportions: penalty box (16.5m deep), goal
      // area, spot at the 11m point INSIDE the box, and the "D" — the part of
      // a circle around the spot that falls outside the box
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2.5;
      const BOX_W = 240, BOX_D = 100;
      const SPOT_Y = GOAL_LINE + Math.round(BOX_D * (11 / 16.5));   // 11m of 16.5m
      ctx.strokeRect(W / 2 - BOX_W / 2, GOAL_LINE, BOX_W, BOX_D);
      ctx.strokeRect(W / 2 - 70, GOAL_LINE, 140, 44);               // goal area
      ctx.beginPath(); ctx.arc(W / 2, SPOT_Y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.fill();
      const dR = 55, boxBot = GOAL_LINE + BOX_D;
      const dA = Math.asin((boxBot - SPOT_Y) / dR);
      ctx.beginPath(); ctx.arc(W / 2, SPOT_Y, dR, dA, Math.PI - dA);
      ctx.stroke();
      ctx.lineWidth = 1;

      // stadion bag målet: aftenhimmel, publikums-bokeh og to lysmaster
      const sky = ctx.createLinearGradient(0, 0, 0, CROSS_Y - 6);
      sky.addColorStop(0, "#131b2c");
      sky.addColorStop(1, "#243149");
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, CROSS_Y - 6);
      const crowd = ["#7c88a8", "#a89078", "#8898b0", "#9a7c88"];
      for (let i = 0; i < 70; i++) {           // publikum (deterministisk)
        ctx.globalAlpha = 0.35 + ((i * 29) % 10) / 30;
        ctx.fillStyle = crowd[i % crowd.length];
        ctx.fillRect((i * 41) % W, 6 + ((i * 17) % (CROSS_Y - 20)), 2.4, 2.4);
      }
      ctx.globalAlpha = 1;
      for (const lx of [30, W - 30]) {         // lysmaster m. glød
        ctx.save();
        ctx.shadowColor = "#fff7d8"; ctx.shadowBlur = 14;
        ctx.fillStyle = "#f4eecb";
        ctx.fillRect(lx - 10, 4, 20, 9);
        ctx.restore();
        ctx.fillStyle = "#5a6478";
        ctx.fillRect(lx - 1.5, 13, 3, CROSS_Y - 21);
      }

      // 3D-mål som NETKASSE set forfra: bagpanelet er et mindre rektangel
      // midt i mundingen med et REGULÆRT kvadratnet (det er dét, øjet
      // genkender som "net"), og loft/sider/gulv forbinder munding og
      // bagpanel med konvergerende strenge — ingen vifte-effekt
      // NETKASSE m. kvadratiske masker og TYNDE strenge; ved mål deformeres
      // bagpanelet af en dæmpet svingning omkring indslagspunktet ("blafr")
      const NI = 16, NT = 12, NB = 6;                  // inset: sider/top/bund
      const bx1 = GOAL_L + NI, bx2 = GOAL_R - NI;
      const by1 = CROSS_Y + NT, by2 = GOAL_LINE - NB;
      // net-deformation: bump ud fra indslag, dør ud på ~1 sek
      const disp = (px, py) => {
        let ox = 0, oy = 0;
        for (const hh of g.netHits) {
          const d2 = ((px - hh.x) ** 2 + (py - hh.y) ** 2) / (38 * 38);
          if (d2 > 4) continue;
          const w2 = Math.exp(-d2) * 9 * Math.exp(-hh.t * 2.8) * Math.cos(hh.t * 19);
          oy -= w2 * 0.75;                             // bulen skyder OP/IND i nettet
          ox += (px - hh.x) * 0.05 * w2;
        }
        return [ox, oy];
      };
      // mørk dybde i hele mundingen
      ctx.fillStyle = "rgba(8,12,24,0.38)";
      ctx.fillRect(GOAL_L, CROSS_Y, GOAL_R - GOAL_L, GOAL_LINE - CROSS_Y);
      // bagpanel: kvadratnet (9px) tegnet som polylinjer, så blafret kan bøje dem
      ctx.strokeStyle = "rgba(255,255,255,0.46)";
      ctx.lineWidth = 0.8;
      const SEGY = 7, SEGX = 8;
      for (let x = bx1; x <= bx2 + 0.1; x += 9) {
        ctx.beginPath();
        for (let k = 0; k <= SEGY; k++) {
          const py = by1 + (k / SEGY) * (by2 - by1);
          const [ox, oy] = disp(x, py);
          k === 0 ? ctx.moveTo(x + ox, py + oy) : ctx.lineTo(x + ox, py + oy);
        }
        ctx.stroke();
      }
      for (let y = by1; y <= by2 + 0.1; y += 9) {
        ctx.beginPath();
        for (let k = 0; k <= SEGX * 4; k++) {
          const px = bx1 + (k / (SEGX * 4)) * (bx2 - bx1);
          const sag = 1.2 * Math.sin(Math.PI * (k / (SEGX * 4)));
          const [ox, oy] = disp(px, y);
          k === 0 ? ctx.moveTo(px + ox, y + sag + oy) : ctx.lineTo(px + ox, y + sag + oy);
        }
        ctx.stroke();
      }
      // loftpanel: tynde strenge fra overliggeren til bagpanelets overkant
      ctx.strokeStyle = "rgba(255,255,255,0.22)";
      for (let i = 0; i <= 14; i++) {
        const tp = i / 14;
        const [ox, oy] = disp(bx1 + tp * (bx2 - bx1), by1);
        ctx.beginPath();
        ctx.moveTo(GOAL_L + tp * (GOAL_R - GOAL_L), CROSS_Y);
        ctx.lineTo(bx1 + tp * (bx2 - bx1) + ox, by1 + oy);
        ctx.stroke();
      }
      for (let i = 1; i < 3; i++) {                    // loftets tværmasker
        const tp = i / 3;
        const yA = CROSS_Y + (by1 - CROSS_Y) * tp;
        ctx.beginPath();
        ctx.moveTo(GOAL_L + NI * tp, yA);
        ctx.lineTo(GOAL_R - NI * tp, yA);
        ctx.stroke();
      }
      // sidepaneler
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      for (let i = 0; i <= 6; i++) {
        const tp = i / 6;
        const yM = CROSS_Y + tp * (GOAL_LINE - CROSS_Y);
        const yB = by1 + tp * (by2 - by1);
        const [oxl, oyl] = disp(bx1, yB);
        const [oxr, oyr] = disp(bx2, yB);
        ctx.beginPath(); ctx.moveTo(GOAL_L, yM); ctx.lineTo(bx1 + oxl, yB + oyl); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(GOAL_R, yM); ctx.lineTo(bx2 + oxr, yB + oyr); ctx.stroke();
      }
      // gulvpanel: svage strenge fra mållinjen til bagpanelets bund
      ctx.strokeStyle = "rgba(255,255,255,0.13)";
      for (let i = 1; i < 7; i++) {
        const tp = i / 7;
        const [ox, oy] = disp(bx1 + tp * (bx2 - bx1), by2);
        ctx.beginPath();
        ctx.moveTo(GOAL_L + tp * (GOAL_R - GOAL_L), GOAL_LINE);
        ctx.lineTo(bx1 + tp * (bx2 - bx1) + ox, by2 + oy);
        ctx.stroke();
      }
      ctx.lineWidth = 1;

      // posts + crossbar i "metal": lys kerne m. mørk kant
      const post = (x, y, w2, h2) => {
        ctx.fillStyle = "#b9bec4"; ctx.fillRect(x, y, w2, h2);
        ctx.fillStyle = "#f7f7f5";
        if (w2 < h2) ctx.fillRect(x + 1, y, w2 - 2.5, h2);      // lodret
        else ctx.fillRect(x, y + 1, w2, h2 - 2.5);              // vandret
      };
      post(GOAL_L - 6, CROSS_Y - 6, 6, GOAL_LINE - CROSS_Y + 6);
      post(GOAL_R, CROSS_Y - 6, 6, GOAL_LINE - CROSS_Y + 6);
      post(GOAL_L - 6, CROSS_Y - 6, GOAL_R - GOAL_L + 12, 6);
      // goal line
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillRect(GOAL_L - 5, GOAL_LINE, GOAL_R - GOAL_L + 10, 3);

      // keeper (red kit) — rotates around his feet into the vain dive
      const kx = g.keepX;
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.beginPath(); ctx.ellipse(kx, GOAL_LINE + 4, g.dive ? 34 : 22, 5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.save();
      ctx.translate(kx, GOAL_LINE);
      if (g.dive) ctx.rotate(g.dive.dir * Math.min(g.dive.t * 2.8, 1) * (Math.PI / 2.05));
      ctx.fillStyle = "#C8352E";                       // shorts + shirt
      ctx.fillRect(-13, -46, 26, 34);
      ctx.fillStyle = "#a02620";
      ctx.fillRect(-11, -14, 22, 8);
      ctx.fillStyle = "#C8352E";                       // arms out
      ctx.fillRect(-KEEP_W, -42, 13, 7);
      ctx.fillRect(KEEP_W - 13, -42, 13, 7);
      ctx.fillStyle = "#e8b48c";                       // hands + head
      ctx.beginPath(); ctx.arc(-KEEP_W + 2, -38, 4.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(KEEP_W - 2, -38, 4.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(0, -54, 8, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#b3541e";                       // hair
      ctx.beginPath(); ctx.arc(0, -57, 7, Math.PI, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#e8e4d8";                       // socks
      ctx.fillRect(-10, -6, 7, 8);
      ctx.fillRect(3, -6, 7, 8);
      ctx.restore();

      // placement zone
      if (g.carry) {
        ctx.fillStyle = "rgba(255,255,255,0.09)";
        ctx.fillRect(8, ZONE_TOP, W - 16, ZONE_BOT - ZONE_TOP);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.moveTo(10, ZONE_TOP); ctx.lineTo(W - 10, ZONE_TOP); ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "rgba(255,255,255,0.22)";
      ctx.setLineDash([3, 8]);
      ctx.beginPath(); ctx.moveTo(12, ZONE_BOT); ctx.lineTo(W - 12, ZONE_BOT); ctx.stroke();
      ctx.setLineDash([]);

      // tray with remaining balls
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r, 0, Math.PI * 2);
      ctx.fillStyle = "#3a3f44"; ctx.fill();
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r - 4, 0, Math.PI * 2);
      ctx.fillStyle = "#23272b"; ctx.fill();
      const inTray = trayCount();
      const show = Math.min(inTray, 2);
      for (let i = 0; i < show; i++)
        drawBallShape(TRAY.x - 11 + i * 22, TRAY.y, BALL_R);
      if (inTray > 2) {
        ctx.font = "800 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "#fff";
        ctx.fillText("×" + inTray, TRAY.x, TRAY.y - TRAY.r - 6);
      }

      // aim guide — bends with the spin you're putting on
      if (g.aim) {
        const { ball, dx, dy, path } = g.aim;
        const len = Math.min(Math.hypot(dx, dy), 190);
        if (len > 10 && dy > 0) {
          const curve = pathCurve(path);
          const ang = Math.atan2(-dy, -dx);
          const gl = 60 + len * 1.3;
          // draw the PREDICTED arc: same spin as the ball, so the guide's tip
          // sweeps to the side the shot will actually bend
          const segs = 12;
          const turnPerSeg = (-curve * 2.6) * 0.45 / segs;   // ≈ total spin over the flight
          let gx = ball.x, gy = ball.y, ga = ang;
          ctx.strokeStyle = "rgba(255,255,255,0.85)";
          ctx.lineWidth = 3; ctx.setLineDash([8, 7]);
          ctx.beginPath();
          ctx.moveTo(gx, gy);
          for (let i = 0; i < segs; i++) {
            ga += turnPerSeg;
            gx += Math.cos(ga) * (gl / segs);
            gy += Math.sin(ga) * (gl / segs);
            ctx.lineTo(gx, gy);
          }
          ctx.stroke(); ctx.setLineDash([]); ctx.lineWidth = 1;
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.fillRect(W / 2 - 60, H - 24, 120, 10);
          ctx.fillStyle = len / 190 > 0.75 ? "#D93A2B" : "#E3B923";
          ctx.fillRect(W / 2 - 60, H - 24, 120 * (len / 190), 10);
          if (Math.abs(curve) > 0.15) {
            ctx.font = "800 12px system-ui, sans-serif";
            ctx.textAlign = "center";
            ctx.fillStyle = "#ffd75e";
            ctx.fillText(t("f_curve") + " " + (curve < 0 ? "→" : "←"), W / 2, H - 32);
          }
        }
      }

      // carried ball
      if (g.carry) {
        ctx.beginPath(); ctx.arc(g.carry.x, g.carry.y + 6, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.fill();
        drawBallShape(g.carry.x, g.carry.y - 4, BALL_R * 1.15);
      }

      // balls
      for (const b of g.balls) {
        if (b.phase === "sinking") {
          // bulging the net
          const k = Math.min(b.sink.t / 0.25, 1);
          const yy = GOAL_LINE - (GOAL_LINE - b.sink.y) * k;
          ctx.globalAlpha = b.sink.t > 0.45 ? Math.max(0, 1 - (b.sink.t - 0.45) / 0.25) : 1;
          drawBallShape(b.sink.x, yy, BALL_R * (1 - k * 0.25));
          ctx.globalAlpha = 1;
          continue;
        }
        ctx.beginPath(); ctx.arc(b.x, b.y + 3, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.fill();
        drawBallShape(b.x, b.y, BALL_R);
      }

      // popups (clamped so wide texts stay on the pitch)
      for (const p of g.popups) {
        const px = Math.max(84, Math.min(W - 84, p.x));
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, px, p.y - p.t * 30);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, px, p.y - p.t * 30);
        ctx.globalAlpha = 1;
      }

      // hat-trick celebration: confetti + arched gold title (pop-in scale)
      if (g.hattrick) {
        const h = g.hattrick;
        const a = h.t < 2.1 ? 1 : Math.max(0, 1 - (h.t - 2.1) / 0.5);
        const pop = Math.min(1, h.t / 0.28);
        const s = 0.55 + 0.45 * (1 - Math.pow(1 - pop, 3));
        ctx.globalAlpha = a;
        for (const f of h.conf) {
          ctx.save(); ctx.translate(f.x, f.y); ctx.rotate(f.r);
          ctx.fillStyle = f.c; ctx.fillRect(-3, -2, 6, 4);
          ctx.restore();
        }
        ctx.save();
        ctx.translate(W / 2, 236);
        ctx.scale(s, s);
        ctx.textAlign = "center";
        ctx.lineJoin = "round";
        let fs = 38;
        ctx.font = `bold ${fs}px 'Alfa Slab One', Georgia, serif`;
        const tw = ctx.measureText(h.title).width;
        if (tw > W - 40) fs = Math.max(20, Math.floor(fs * (W - 40) / tw));
        ctx.font = `bold ${fs}px 'Alfa Slab One', Georgia, serif`;
        ctx.lineWidth = 9; ctx.strokeStyle = "#7e1f1a";
        ctx.strokeText(h.title, 0, 0);
        ctx.fillStyle = "#E3B923";
        ctx.fillText(h.title, 0, 0);
        ctx.font = "bold 17px 'Alfa Slab One', Georgia, serif";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.45)";
        ctx.strokeText(h.sub, 0, 32);
        ctx.fillStyle = "#F6EED9";
        ctx.fillText(h.sub, 0, 32);
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      // banner — font scales down until the text fits the lane
      if (g.banner) {
        const a = g.banner.t < 1.4 ? 1 : 1 - (g.banner.t - 1.4) / 0.4;
        ctx.globalAlpha = Math.max(0, a);
        let fs = 22;
        ctx.font = `bold ${fs}px 'Alfa Slab One', Georgia, serif`;
        const tw = ctx.measureText(g.banner.txt).width;
        if (tw > W - 36) fs = Math.max(12, Math.floor(fs * (W - 36) / tw));
        ctx.font = `bold ${fs}px 'Alfa Slab One', Georgia, serif`;
        ctx.textAlign = "center";
        ctx.lineWidth = 6; ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.strokeText(g.banner.txt, W / 2, 250);
        ctx.fillStyle = "#ffd75e";
        ctx.fillText(g.banner.txt, W / 2, 250);
        ctx.globalAlpha = 1;
      }

      // hint
      if (!g.aim && !g.carry && g.balls.length === 0 && g.shots < SHOTS + g.bonusShots) {
        ctx.font = "600 13px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.fillText(t("f_hint"), W / 2 - 20, H - 12, W - 96);
      }

      // aften-vignette (stadion-dybde)
      const vg = ctx.createRadialGradient(W / 2, H * 0.42, 200, W / 2, H * 0.42, 430);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(5,10,20,0.24)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
    };

    let raf, last = performance.now();
    const loop = (t) => {
      const dt = Math.min((t - last) / 1000, 0.033);
      last = t;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
    };
  }, [phase]);

  /* ================= UI ================= */
  const prizeSize = prizeForGoals(finalGoals);

  return (
    <div className={"fb-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_football").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("f_sign2", { shots: SHOTS, need: NEED })}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("f_lede", { shots: SHOTS })}</p>
          <ul className="rules">
            <li>{t("f_rule1", { shots: SHOTS, need: NEED })}</li>
            <li>{t("f_rule2")}</li>
            <li>{t("f_rule3")}</li>
            <li>{t("f_rule4")}</li>
          </ul>
          <p className="prizenote">{t("f_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("f_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("f_goals")} <b>{hud.goals}</b></span>
            <span className="last">{t("f_shots", { n: Math.min(hud.shots, SHOTS), max: SHOTS })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && !claimed && (
        <PrizePicker size={prizeSize} game="Fodbold" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("f_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("f_trophyline", { shots: SHOTS })}</p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "finished" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("f_win")}</div>
            <p className="winline">{t("f_winline", { goals: finalGoals, shots: finalShots })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {offer && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("ad_offer_cans_title")}</div>
            <p className="winline">{t("ad_offer_football")}</p>
            <button className="cta gold" onClick={watchAd}>{t("ad_watch")}</button>
            {adErr && <p className="adfail">{t("ad_fail")}</p>}
            <button className="cta alt" onClick={declineOffer}>{t("ad_no")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("f_fail")}</div>
            <p className="winline">{t("f_failline", { goals: finalGoals, shots: finalShots, need: NEED })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.fb-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#1c3317;
  background-image:radial-gradient(circle at 50% -10%, #35592a 0%, #1c3317 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(28px,8vw,46px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.adfail{font-size:12px; font-weight:800; opacity:.7; margin:8px 0 0;}
.cta.alt{margin-top:10px; background:#5c7a4a; box-shadow:0 4px 0 #37492c; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #37492c;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #cfd4d8; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(10,20,8,.72); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#37492c;}
.ribbon.gold{background:linear-gradient(#d4a92c, #a67c14); color:#fff8e2; text-shadow:0 1px 2px rgba(90,60,0,.6);}
.trophybig{font-size:74px; line-height:1.1; margin:2px 0 6px; filter:drop-shadow(0 6px 8px rgba(0,0,0,.3)); display:flex; justify-content:center;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
